import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, BadgePercent, CheckCircle2 } from "lucide-react";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const eur = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; } };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discountFor, setDiscountFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/orders"); setOrders(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (o) => {
    try { await api.post(`/admin/orders/${o.id}/mark-paid`); toast.success("Ordine segnato come pagato."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const paidTotal = orders.filter((o) => o.status === "paid").reduce((a, o) => a + (o.total || 0), 0);
  const pendingList = orders.filter((o) => o.status === "pending");
  const pendingTotal = pendingList.reduce((a, o) => a + (o.total || 0), 0);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="admin-orders">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl text-brand-ink mb-6">Ordini</h1>

        {!loading && orders.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <MetricCard label="Incassato" value={eur(paidTotal)} accent="green" />
            <MetricCard label="Ordini in attesa" value={pendingList.length} accent="gold" />
            <MetricCard label="Valore in attesa" value={eur(pendingTotal)} />
            <MetricCard label="Ordini totali" value={orders.length} />
          </div>
        )}

        <div className="bg-white border border-brand-line rounded-sm p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-brand-muted py-8 justify-center">
              <Loader2 className="animate-spin" size={18} /> Caricamento…
            </div>
          ) : orders.length === 0 ? (
            <p className="text-sm text-brand-muted py-6 text-center">Nessun ordine.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Data</TableHead><TableHead>Cliente</TableHead>
                <TableHead>Corsi</TableHead><TableHead>Subtotale</TableHead>
                <TableHead>Sconto</TableHead><TableHead>Totale</TableHead>
                <TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="text-sm">{fmtDate(o.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      {o.organization_name || o.buyer_email}
                      <div className="text-xs text-brand-muted">{o.buyer_kind === "organization" ? "Azienda" : "Privato"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{o.items.map((i) => `${i.course_title} ×${i.seats}`).join(", ")}</TableCell>
                    <TableCell>{eur(o.subtotal)}</TableCell>
                    <TableCell className="text-sm">{o.manual_discount ? discountLabel(o) : "—"}</TableCell>
                    <TableCell className="font-medium">{eur(o.total)}</TableCell>
                    <TableCell>{statusPill(o.status)}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      {o.status === "pending" && (
                        <>
                          <Button variant="outline" size="sm" className="h-8" onClick={() => setDiscountFor(o)}>
                            <BadgePercent size={13} className="mr-1" /> Sconto
                          </Button>
                          <Button size="sm" className="btn-primary h-8" onClick={() => markPaid(o)}>
                            <CheckCircle2 size={13} className="mr-1" /> Segna pagato
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <DiscountDialog order={discountFor} onClose={() => setDiscountFor(null)} onDone={() => { setDiscountFor(null); load(); }} />
    </div>
  );
}

function DiscountDialog({ order, onClose, onDone }) {
  const [mode, setMode] = useState("pct");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMode("pct"); setValue(""); setReason(""); }, [order]);
  if (!order) return null;

  const apply = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { reason };
      if (mode === "pct") body.pct = Number(value); else body.amount = Number(value);
      await api.post(`/admin/orders/${order.id}/apply-discount`, body);
      toast.success("Sconto applicato.");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Autorizza uno sconto</DialogTitle>
          <DialogDescription>
            Subtotale ordine: <strong>{eur(order.subtotal)}</strong>. Lo sconto è tracciato con la tua autorizzazione.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={apply} className="space-y-4">
          <div className="flex gap-2">
            <Button type="button" variant={mode === "pct" ? "default" : "outline"}
              className={mode === "pct" ? "btn-primary h-9" : "h-9"} onClick={() => setMode("pct")}>Percentuale</Button>
            <Button type="button" variant={mode === "amount" ? "default" : "outline"}
              className={mode === "amount" ? "btn-primary h-9" : "h-9"} onClick={() => setMode("amount")}>Importo €</Button>
          </div>
          <div>
            <Label className="text-sm text-brand-ink">{mode === "pct" ? "Sconto (%)" : "Sconto (€)"}</Label>
            <Input type="number" min={0} step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
              className="mt-1.5 h-11 rounded-sm border-brand-line" required />
          </div>
          <div>
            <Label className="text-sm text-brand-ink">Motivazione</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="es. Offerta lancio"
              className="mt-1.5 h-11 rounded-sm border-brand-line" required />
          </div>
          <DialogFooter>
            <Button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Applica sconto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MetricCard({ label, value, accent }) {
  const color = accent === "green" ? "text-emerald-700" : accent === "gold" ? "text-brand-gold-deep" : "text-brand-ink";
  return (
    <div className="bg-white border border-brand-line rounded-sm px-4 py-3">
      <div className="text-xs text-brand-muted">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function statusPill(s) {
  const map = {
    paid: "bg-green-50 text-green-700 border-green-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const label = { paid: "Pagato", cancelled: "Annullato", pending: "In attesa" }[s] || s;
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-sm border ${map[s] || map.pending}`}>{label}</span>;
}
function discountLabel(o) {
  const d = o.manual_discount;
  if (d.pct != null) return `−${d.pct}%`;
  if (d.amount != null) return `−${eur(d.amount)}`;
  return "—";
}
